import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import checkIcon from "../../../../../assets/pictures/icons/check-icon.svg";
import type { IRootState } from "../../../../../redux/store";
import styles from "./BackgroundSelection.module.scss";
import gradient from "../../../../../assets/pictures/scene/gradient.svg";
import orange from "../../../../../assets/pictures/scene/orange.svg";
import white from "../../../../../assets/pictures/scene/white.svg";
import errorSceneView from "../../../../../assets/pictures/errorSceneView.svg";
import { changeFormData } from "../../../../../redux/slices/formSlice";
import Loader from "../../../../Spinner/Spinner";
import { useTranslation } from "react-i18next";

interface Scene {
	value: string;
	description: string;
}

const sceneImages: Record<string, string> = {
	"gradient": gradient,
	"orange": orange,
	"white": white,
};

export const BackgroundSelection: React.FC = () => {
	const { t } = useTranslation('form');
	const formData = useSelector((state: IRootState) => state.avatarForm);
	const dispatch = useDispatch();
	const scenes: Scene[] = useSelector((state: IRootState) => state.platformScenes);
	const currentScenes = useMemo(() => {
		return scenes.map((el) => ({
			...el,
			sceneImg: sceneImages[el.description],
		}));
	}, [scenes]);

	const [loadedImages, setLoadedImages] = useState<Record<string, "loading" | "loaded" | "error">>(
        () => currentScenes.reduce((acc, scene) => ({ ...acc, [scene.value]: "loading" }), {})
    );

	const [selectedBackground, setSelectedBackground] = useState<string | null>(
		currentScenes.length > 0 ? formData.scene : null,
	);

	const handleSelect = (value: string) => {
		setSelectedBackground(value);
		dispatch(changeFormData({ scene: value }));
	};

	const handleImageLoad = (value: string) => {
        setLoadedImages((prev) => ({ ...prev, [value]: "loaded" }));
    };

	const handleImageError = (value: string) => {
        setLoadedImages((prev) => ({ ...prev, [value]: "error" }));
    };

	return (
		<div className={styles.backgroundContainer}>
			<h5>{t("choose_background")}</h5>
			{currentScenes.length > 0 && (
				<div className={styles.backgrounds}>
					{currentScenes.map((el) => {
						const isSelected = selectedBackground === el.value;
						const imageStatus = loadedImages[el.value];
						return (
							<label
								key={el.value}
								className={`${styles.backgroundOption} ${isSelected ? styles.selected : ""}`}
							>
								{imageStatus === "loaded" && (
									<input
										type="radio"
										name="background"
										value={el.value}
										checked={isSelected}
										onChange={() => handleSelect(el.value)}
										className={styles.hiddenRadio}
									/>
								)}
								{imageStatus === "loading" && (
									<div className={styles.spinnerWrapper}>
										<Loader size="small" />
									</div>
								)}
								<img
                                    src={
                                        imageStatus === "error"
                                            ? errorSceneView
                                            : el.sceneImg
                                    }
                                    alt={
                                        imageStatus === "error"
                                            ? "error scene view"
                                            : el.description
                                    }
									onLoad={() => handleImageLoad(el.value)}
                                    onError={() => handleImageError(el.value)}
                                />
								{isSelected && imageStatus === "loaded" && (
									<div className={styles.checkmark}>
										<img src={checkIcon} alt="check icon" />
									</div>
								)}
							</label>
						);
					})}
				</div>
			)}
		</div>
	);
};
